/**
 * Selector de icono personalizado para nodos
 * @module components/forms/IconPicker
 */

import { useState } from 'react'
import { Image, Smile, X, Check } from 'lucide-react'

interface IconPickerProps {
  value?: string
  onChange: (icon: string | undefined) => void
  nodeType: string
}

// Emojis sugeridos por categoría
const EMOJI_CATEGORIES = {
  'Infraestructura': ['🏢', '🏭', '🏗️', '🏠', '🏡', '🏘️', '🏚️', '🏛️', '⛪', '🕌'],
  'Tecnología': ['📡', '📶', '🔌', '💡', '🖥️', '📱', '💻', '🖨️', '🔧', '⚙️'],
  'Señales': ['🚦', '🚧', '⚠️', '🔴', '🟢', '🟡', '🔵', '⭐', '💫', '✨'],
  'Transporte': ['🚗', '🚌', '🏍️', '🚲', '✈️', '🚀', '🛸', '⛵', '🚁', '🚂'],
  'Naturaleza': ['🌳', '🌲', '🌴', '🌵', '🌾', '🌻', '🏔️', '⛰️', '🗻', '🌊'],
  'Lugares': ['📍', '🗺️', '🧭', '🎯', '📌', '🏁', '🚩', '🎪', '⛺', '🏕️'],
  'Otros': ['👤', '👥', '🏃', '🧑‍💼', '👷', '🔒', '🔓', '🔑', '💼', '📦'],
}

// Emojis recomendados por tipo de nodo
const RECOMMENDED_BY_TYPE: Record<string, string[]> = {
  OLT: ['📡', '🖥️', '🏢', '⚙️', '🔌'],
  POP: ['🏢', '🏭', '📡', '🖥️', '🏗️'],
  NAP: ['📦', '🔌', '📶', '🎯', '📍'],
  CIERRE: ['📦', '🔧', '⚙️', '🔗', '🔌'],
  CLIENTE: ['🏠', '🏡', '👤', '📱', '💻'],
  POSTE: ['🏗️', '💡', '🚧', '📍', '🔌'],
}

export function IconPicker({ value, onChange, nodeType }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'emoji' | 'url'>('emoji')
  const [customUrl, setCustomUrl] = useState(value?.startsWith('http') ? value : '')
  const [selectedEmoji, setSelectedEmoji] = useState(value && !value.startsWith('http') ? value : '')

  const recommended = RECOMMENDED_BY_TYPE[nodeType] || RECOMMENDED_BY_TYPE.NAP

  const handleSelectEmoji = (emoji: string) => {
    setSelectedEmoji(emoji)
    setCustomUrl('')
    onChange(emoji)
    setIsOpen(false)
  }

  const handleApplyUrl = () => {
    if (customUrl.trim()) {
      setSelectedEmoji('')
      onChange(customUrl.trim())
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    setSelectedEmoji('')
    setCustomUrl('')
    onChange(undefined)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-1">Icono Personalizado (Opcional)</label>
      
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 px-3 py-2 border rounded-lg text-left flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-dark-700 dark:border-dark-600"
        >
          {value ? (
            value.startsWith('http') || value.startsWith('data:') ? (
              <img src={value} alt="icon" className="w-6 h-6 object-contain" />
            ) : (
              <span className="text-xl">{value}</span>
            )
          ) : (
            <span className="text-gray-400 flex items-center gap-2">
              <Smile className="w-5 h-5" />
              Seleccionar icono...
            </span>
          )}
        </button>
        
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-2 border rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 dark:border-dark-600"
            title="Quitar icono"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Panel de selección */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-dark-800 border rounded-lg shadow-xl dark:border-dark-600 max-h-[400px] overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b dark:border-dark-600">
            <button
              type="button"
              onClick={() => setActiveTab('emoji')}
              className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'emoji'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Smile className="w-4 h-4" />
              Emojis
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('url')}
              className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'url'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Image className="w-4 h-4" />
              URL Imagen
            </button>
          </div>

          <div className="p-3 max-h-[320px] overflow-y-auto">
            {activeTab === 'emoji' && (
              <div className="space-y-4">
                {/* Recomendados */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Recomendados para {nodeType}</p>
                  <div className="flex flex-wrap gap-1">
                    {recommended.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleSelectEmoji(emoji)}
                        className={`w-10 h-10 text-xl rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center justify-center ${
                          selectedEmoji === emoji ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500' : ''
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Categorías */}
                {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                  <div key={category}>
                    <p className="text-xs font-medium text-gray-500 mb-2">{category}</p>
                    <div className="flex flex-wrap gap-1">
                      {emojis.map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => handleSelectEmoji(emoji)}
                          className={`w-8 h-8 text-lg rounded hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center justify-center ${
                            selectedEmoji === emoji ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500' : ''
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'url' && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-2">
                    Ingresa la URL de una imagen (PNG, JPG, SVG)
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      placeholder="https://ejemplo.com/icono.png"
                      className="flex-1 px-3 py-2 text-sm border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                    />
                    <button
                      type="button"
                      onClick={handleApplyUrl}
                      disabled={!customUrl.trim()}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Preview */}
                {customUrl && (
                  <div className="border rounded-lg p-4 text-center dark:border-dark-600">
                    <p className="text-xs text-gray-500 mb-2">Vista previa</p>
                    <img 
                      src={customUrl} 
                      alt="preview" 
                      className="w-16 h-16 mx-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="red"><text x="4" y="18" font-size="14">❌</text></svg>'
                      }}
                    />
                  </div>
                )}

                <div className="text-xs text-gray-500 space-y-1">
                  <p className="font-medium">Consejos:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Usa imágenes cuadradas (ej: 64x64 px)</li>
                    <li>Formato PNG con fondo transparente funciona mejor</li>
                    <li>Puedes usar servicios como Imgur o Cloudinary</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Cerrar */}
          <div className="border-t dark:border-dark-600 p-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
